import { ShieldCheck, Sparkle, UserCircle } from "@phosphor-icons/react";
import { useState } from "react";
import { useCustomer } from "../../context/CustomerContext";
import { MagneticButton } from "../MagneticButton";

type Mode = "login" | "register";

interface CustomerAuthCardProps {
  compact?: boolean;
  title?: string;
  text?: string;
}

export function CustomerAuthCard({
  compact = false,
  title = "Войти в личный кабинет",
  text = "Сохраненные адреса, история заказов, быстрый repeat и настройки кухни откроются сразу после входа."
}: CustomerAuthCardProps) {
  const { login, register, isLoading } = useCustomer();
  const [mode, setMode] = useState<Mode>("login");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ tone: "idle" | "error" | "success"; text: string }>({
    tone: "idle",
    text: ""
  });
  const [loginForm, setLoginForm] = useState({
    login: "",
    password: ""
  });
  const [registerForm, setRegisterForm] = useState({
    name: "",
    phone: "",
    email: "",
    password: ""
  });

  const submitLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({
      tone: "idle",
      text: "Проверяем доступ и подгружаем кабинет."
    });

    try {
      await login(loginForm);
      setStatus({
        tone: "success",
        text: "Вход выполнен. Кабинет уже открыт."
      });
    } catch (error) {
      setStatus({
        tone: "error",
        text: error instanceof Error ? error.message : "Не удалось войти в кабинет."
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitRegister = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({
      tone: "idle",
      text: "Создаем кабинет и сохраняем ваш профиль."
    });

    try {
      await register(registerForm);
      setStatus({
        tone: "success",
        text: "Кабинет создан. Теперь можно оформлять заказы и хранить адреса."
      });
    } catch (error) {
      setStatus({
        tone: "error",
        text: error instanceof Error ? error.message : "Не удалось создать кабинет."
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article
      className={[
        "rounded-[2.3rem] border border-white/72 bg-white/82 shadow-[0_34px_78px_-46px_rgba(27,53,52,0.56)] backdrop-blur-2xl",
        compact ? "p-5" : "p-6 md:p-7"
      ].join(" ")}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="max-w-[34rem]">
          <p className="inline-flex items-center gap-2 rounded-full border border-teal-900/10 bg-teal-900/[0.05] px-3 py-1.5 text-[0.72rem] uppercase tracking-[0.18em] text-teal-800">
            <Sparkle size={14} weight="fill" />
            личный кабинет
          </p>
          <h2 className="mt-4 text-[1.8rem] font-semibold leading-[0.96] tracking-[-0.06em] text-stone-950">
            {title}
          </h2>
          <p className="mt-3 text-sm leading-7 text-stone-600">{text}</p>
        </div>

        <div className="rounded-[1.7rem] bg-[linear-gradient(160deg,#134042_0%,#0d2829_100%)] p-4 text-stone-50 shadow-[0_22px_56px_-36px_rgba(12,35,36,0.72)]">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-[1.2rem] border border-white/10 bg-white/10">
              <UserCircle size={22} />
            </div>
            <div>
              <p className="text-sm font-semibold tracking-tight">Быстрый вход</p>
              <p className="mt-1 text-xs leading-5 text-stone-300">
                Почта или телефон, сохраненные адреса и история заказов.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 inline-flex rounded-full border border-white/70 bg-stone-50/80 p-1">
        {[
          { id: "login", label: "Вход" },
          { id: "register", label: "Регистрация" }
        ].map((item) => {
          const active = mode === item.id;

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setMode(item.id as Mode);
                setStatus({ tone: "idle", text: "" });
              }}
              className={[
                "rounded-full px-4 py-2.5 text-sm font-medium transition duration-300 active:scale-[0.98]",
                active ? "bg-teal-700 text-stone-50" : "text-stone-600"
              ].join(" ")}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {mode === "login" ? (
        <form onSubmit={submitLogin} className="mt-6 grid gap-4">
          <label className="grid gap-2 text-sm text-stone-600">
            E-mail или телефон
            <input
              value={loginForm.login}
              onChange={(event) => setLoginForm((current) => ({ ...current, login: event.target.value }))}
              placeholder="mail@example.com или +7 999 123 45 67"
              className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 placeholder:text-stone-400 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
            />
          </label>

          <label className="grid gap-2 text-sm text-stone-600">
            Пароль
            <input
              type="password"
              value={loginForm.password}
              onChange={(event) => setLoginForm((current) => ({ ...current, password: event.target.value }))}
              placeholder="Введите пароль"
              className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 placeholder:text-stone-400 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
            />
          </label>

          <StatusRow status={status} />

          <MagneticButton type="submit" fullWidth disabled={submitting || isLoading}>
            {submitting ? "Входим..." : "Открыть кабинет"}
          </MagneticButton>
        </form>
      ) : (
        <form onSubmit={submitRegister} className="mt-6 grid gap-4 md:grid-cols-2">
          <label className="grid gap-2 text-sm text-stone-600 md:col-span-2">
            Имя
            <input
              value={registerForm.name}
              onChange={(event) => setRegisterForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="Например, Мадина"
              className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 placeholder:text-stone-400 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
            />
          </label>

          <label className="grid gap-2 text-sm text-stone-600">
            Телефон
            <input
              value={registerForm.phone}
              onChange={(event) => setRegisterForm((current) => ({ ...current, phone: event.target.value }))}
              placeholder="+7 999 123 45 67"
              className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 placeholder:text-stone-400 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
            />
          </label>

          <label className="grid gap-2 text-sm text-stone-600">
            E-mail (необязательно)
            <input
              type="email"
              value={registerForm.email}
              onChange={(event) => setRegisterForm((current) => ({ ...current, email: event.target.value }))}
              placeholder="Если хотите, укажите почту"
              className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 placeholder:text-stone-400 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
            />
          </label>

          <label className="grid gap-2 text-sm text-stone-600 md:col-span-2">
            Пароль
            <input
              type="password"
              value={registerForm.password}
              onChange={(event) => setRegisterForm((current) => ({ ...current, password: event.target.value }))}
              placeholder="Минимум 6 символов"
              className="rounded-[1.25rem] border border-teal-900/10 bg-white/84 px-4 py-3 text-base text-stone-950 outline-none transition duration-300 placeholder:text-stone-400 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/10"
            />
          </label>

          <div className="md:col-span-2">
            <StatusRow status={status} />
          </div>

          <div className="md:col-span-2">
            <MagneticButton type="submit" fullWidth disabled={submitting || isLoading}>
              {submitting ? "Создаем кабинет..." : "Создать кабинет"}
            </MagneticButton>
          </div>
        </form>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-3">
        {[
          "Сохраняем адреса и любимые сценарии доставки.",
          "История заказов доступна сразу после оформления.",
          "Настройки halal, соусов и подачи хранятся в профиле."
        ].map((line) => (
          <div
            key={line}
            className="rounded-[1.4rem] border border-stone-900/8 bg-stone-50/84 px-4 py-3 text-sm leading-6 text-stone-600"
          >
            <span className="inline-flex items-center gap-2 font-medium text-stone-900">
              <ShieldCheck size={14} className="text-teal-700" />
              {line}
            </span>
          </div>
        ))}
      </div>
    </article>
  );
}

function StatusRow({
  status
}: {
  status: {
    tone: "idle" | "error" | "success";
    text: string;
  };
}) {
  return (
    <div
      className={[
        "min-h-6 text-sm leading-7",
        status.tone === "error" ? "text-rose-600" : status.tone === "success" ? "text-emerald-700" : "text-stone-500"
      ].join(" ")}
    >
      {status.text}
    </div>
  );
}
